import { Fragment, useEffect, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Button,
  Callout,
  ChevronLeftIcon,
  ChevronRightIcon,
  DateInput,
  FilterIcon,
  Label,
  ListEmptyState,
  LoadingSpinner,
  Select,
  TextInput,
  XIcon,
} from '../../components/ui';
import { t } from '../../i18n';
import {
  ApiError,
  api,
  type AuditLogEntry,
  type AuditLogListResponse,
} from '../../services/api';

function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('el-GR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function AuditLogsPage() {
  const labels = t().auditLogs;
  const [data, setData] = useState<AuditLogListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [actions, setActions] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  async function loadFilters() {
    try {
      const filters = await api.getAuditLogFilters();
      setActions(filters.actions);
      setEntityTypes(filters.entityTypes);
    } catch {
      // non-blocking
    }
  }

  async function load(
    nextPage = page,
    overrides?: Partial<{
      search: string;
      action: string;
      entityType: string;
      from: string;
      to: string;
    }>,
  ) {
    setLoading(true);
    setError(null);
    const q = {
      search: overrides?.search ?? search,
      action: overrides?.action ?? action,
      entityType: overrides?.entityType ?? entityType,
      from: overrides?.from ?? from,
      to: overrides?.to ?? to,
    };
    try {
      const result = await api.getAuditLogs({
        page: nextPage,
        pageSize: 50,
        search: q.search.trim() || undefined,
        action: q.action || undefined,
        entityType: q.entityType || undefined,
        from: q.from || undefined,
        to: q.to || undefined,
      });
      setData(result);
      setPage(result.page);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadFilters();
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyFilters() {
    void load(1);
  }

  function clearFilters() {
    setSearch('');
    setAction('');
    setEntityType('');
    setFrom('');
    setTo('');
    void load(1, {
      search: '',
      action: '',
      entityType: '',
      from: '',
      to: '',
    });
  }

  const actionOptions = [
    { value: '', label: labels.allActions },
    ...actions.map((a) => ({ value: a, label: a })),
  ];
  const entityOptions = [
    { value: '', label: labels.allEntities },
    ...entityTypes.map((e) => ({ value: e, label: e })),
  ];

  return (
    <PageShell>
      <p className="text-sm text-muted">{labels.hint}</p>

      <ListFiltersBar
        actions={
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
              <XIcon className="h-3.5 w-3.5" />
              {t().common.clearFilters}
            </Button>
            <Button type="button" size="sm" onClick={applyFilters}>
              <FilterIcon className="h-3.5 w-3.5" />
              {labels.applyFilters}
            </Button>
          </div>
        }
      >
        <div className="grid w-full gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label>{labels.search}</Label>
            <TextInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={labels.searchPlaceholder}
              showClearButton
            />
          </div>
          <div>
            <Label>{labels.action}</Label>
            <Select
              value={action}
              options={actionOptions}
              showClearButton={false}
              onChange={(e) => setAction(e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.entityType}</Label>
            <Select
              value={entityType}
              options={entityOptions}
              showClearButton={false}
              onChange={(e) => setEntityType(e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.from}</Label>
            <DateInput
              value={from}
              showClearButton
              showQuickFill={false}
              showFormatHint={false}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <Label>{labels.to}</Label>
            <DateInput
              value={to}
              showClearButton
              showQuickFill={false}
              showFormatHint={false}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
      </ListFiltersBar>

      {error ? <Callout variant="danger">{error}</Callout> : null}

      <DataTablePanel className="min-h-0 flex-1">
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : !data || data.items.length === 0 ? (
          <ListEmptyState title={labels.empty} />
        ) : (
          <>
            <table className="min-w-full text-left text-sm">
              <thead className={dataTableHeadClass}>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
                  <th className="px-3 py-2 font-semibold">{labels.colWhen}</th>
                  <th className="px-3 py-2 font-semibold">{labels.colActor}</th>
                  <th className="px-3 py-2 font-semibold">{labels.colAction}</th>
                  <th className="px-3 py-2 font-semibold">{labels.colEntity}</th>
                  <th className="px-3 py-2 font-semibold">{labels.colSummary}</th>
                  <th className="px-3 py-2 font-semibold">{labels.colIp}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row: AuditLogEntry) => {
                  const open = expandedId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <tr
                        className="cursor-pointer border-b border-line hover:bg-surface/70"
                        onClick={() =>
                          setExpandedId(open ? null : row.id)
                        }
                      >
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted">
                          {formatDateTime(row.createdAt)}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-ink">
                            {row.actorEmail || '—'}
                          </div>
                          <div className="text-xs text-muted">
                            {row.actorRole || '—'}
                            {row.tenantId != null
                              ? ` · tenant #${row.tenantId}`
                              : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs">
                          {row.action}
                        </td>
                        <td className="px-3 py-2.5 text-xs">
                          {row.entityType || '—'}
                          {row.entityId != null ? ` #${row.entityId}` : ''}
                        </td>
                        <td className="max-w-xs px-3 py-2.5">
                          <span className="line-clamp-2">{row.summary}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted">
                          {row.ipAddress || '—'}
                        </td>
                      </tr>
                      {open ? (
                        <tr className="border-b border-line bg-surface/40">
                          <td colSpan={6} className="px-3 py-3">
                            <div className="grid gap-2 text-xs sm:grid-cols-2">
                              <div>
                                <span className="text-muted">{labels.colMethod}: </span>
                                {row.method || '—'} {row.path || ''}
                              </div>
                              <div>
                                <span className="text-muted">{labels.colStatus}: </span>
                                {row.statusCode ?? '—'}
                              </div>
                              <div className="sm:col-span-2">
                                <span className="text-muted">{labels.colUserAgent}: </span>
                                {row.userAgent || '—'}
                              </div>
                              <div className="sm:col-span-2">
                                <span className="mb-1 block text-muted">
                                  {labels.colMetadata}
                                </span>
                                <pre className="max-h-48 overflow-auto rounded-lg border border-line bg-panel p-3 text-[11px] leading-relaxed">
                                  {row.metadata
                                    ? JSON.stringify(row.metadata, null, 2)
                                    : '—'}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className="flex items-center justify-between gap-3 border-t border-line px-3 py-2 text-sm">
              <span className="text-muted">
                {labels.pageOf
                  .replace('{page}', String(data.page))
                  .replace('{total}', String(data.totalPages))
                  .replace('{count}', String(data.total))}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={data.page <= 1 || loading}
                  onClick={() => void load(data.page - 1)}
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                  {labels.prev}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={data.page >= data.totalPages || loading}
                  onClick={() => void load(data.page + 1)}
                >
                  {labels.next}
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
